import { Ref, watch, nextTick } from 'vue';
import { GridApi, GridOptions, ColDef, ColumnApi } from 'ag-grid-community';
import { isFunction, cloneDeep } from 'lodash-es';
import { copyObject } from '../../core/Common';

// ag-grid的ColDef定义，增加两项响应式:
// 1. headerName为Ref<string>
// 2. forceHide:Ref<boolean> 是否强制隐藏列
export function useAgColumnReactive(
  realColDefs: ColDef<any>[],
  gridOptions: GridOptions,
  visibles: Record<string, boolean | 'deleted'>, // 列是否显示

  columns: { name: string; title: string }[] // field => name映射
) {
  // hide的原值
  const oldHideMap: Record<string, boolean | undefined> = {};
  // 是否强制隐藏
  const forceHideColumns: string[] = [];

  let agApi: GridApi;
  let columnApi: ColumnApi;

  let pendingNameMap: Record<string, string> = {};
  let pendingVisibleMap: Record<string, boolean> = {};

  const onGridReady = gridOptions.onGridReady;
  gridOptions.onGridReady = (event) => {
    agApi = event.api;
    columnApi = event.columnApi;

    onGridReady?.(event);

    // 更新缓存的
    for (const [fieldName, newName] of Object.entries(pendingNameMap)) {
      const colDef = agApi.getColumnDef(fieldName);
      if (colDef) {
        colDef.headerName = newName;
      }
    }

    if (Object.keys(pendingNameMap).length) {
      agApi.refreshHeader();
      pendingNameMap = {};
    }

    for (const [fieldName, newVisible] of Object.entries(pendingVisibleMap)) {
      columnApi.setColumnVisible(fieldName, newVisible);
    }

    pendingVisibleMap = {};
  };

  // 处理ColDef中reactive属性
  for (const field of realColDefs) {
    // 字段名
    const fieldName = field.field!;

    // headerName reactive处理
    if (isFunction(field.headerName)) {
      const nameRef = field.headerName() as Ref<string>;
      // 初始化
      field.headerName = nameRef.value;

      watch(nameRef, (newName) => {
        field.headerName = newName;

        if (agApi) {
          const colDef = agApi.getColumnDef(fieldName);
          if (colDef) {
            colDef.headerName = newName;
            agApi.refreshHeader();
          }
        } else {
          // ag 没有ready, 缓存起来
          pendingNameMap[fieldName] = newName;
        }

        // 更新columns中更名
        if (columns) {
          const column = columns.find((e) => e.name == fieldName);
          if (column) column.title = newName;
        }
      });
    }

    // forceHide reactive处理
    if ((field as any).forceHide) {
      // 记录forceHide列
      forceHideColumns.push(fieldName);

      // 记录原值
      oldHideMap[fieldName] = field.hide;

      const forceHide = (field as any).forceHide() as Ref<boolean>;
      Reflect.deleteProperty(field, 'forceHide');

      if (forceHide.value) {
        field.hide = true;
        visibles[fieldName] = 'deleted';
      }

      watch(forceHide, (newForce) => {
        // 从visibles删除, 使列配置界面不显示
        if (newForce) {
          visibles[fieldName] = 'deleted';
        } else {
          visibles[fieldName] = !oldHideMap[fieldName];
        }

        if (columnApi) {
          nextTick(() => {
            // 更新列显示
            columnApi.setColumnVisible(
              fieldName,
              newForce ? false : !oldHideMap[fieldName]
            );
          });
        } else {
          // ag 没有ready, 缓存起来
          pendingVisibleMap[fieldName] = newForce
            ? false
            : !oldHideMap[fieldName];
        }
      });
    }
  }

  // 修改oldHide
  function setColumnOldHide(name: string, hide: boolean | undefined) {
    if (forceHideColumns.includes(name)) {
      oldHideMap[name] = hide;
    }
  }

  function getColumnOldHide(col: ColDef<any>) {
    const name = col.field!;
    if (forceHideColumns.includes(name)) {
      return oldHideMap[name];
    }

    return col.hide;
  }

  // 复制一份用于恢复
  const oldHideMapBak = cloneDeep(oldHideMap);
  function restoreColumnOldHide() {
    copyObject(oldHideMap, oldHideMapBak);
  }

  return { getColumnOldHide, setColumnOldHide, restoreColumnOldHide };
}
